/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useEffect } from 'react';
import { NodeProps, Position, Handle } from 'reactflow';
import { useDispatch } from 'react-redux';
import NodeHeader from 'src/components/ui/NodeHeader/NodeHeader';
import Input from 'src/components/ui/Input/Input';
import { useTranslation } from 'react-i18next';
import { setNodeConfiguration } from 'src/redux/workflows/slice';

const NotificationBuilderNode: React.FC<NodeProps> = ({ id, data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [titleValue, setTitleValue] = useState<string>('');
  const [messageValue, setMessageValue] = useState<string>('');

  // Initialize state with existing data
  useEffect(() => {
    if (data.actionData) {
      const { title, message } = data.actionData;

      if (title && title !== titleValue) {
        setTitleValue(title);
      }

      if (message && message !== messageValue) {
        setMessageValue(message);
      }
    }
  }, [data.actionData]);

  const handleResetClick = () => {
    if (data.isEditMode && data.removeNode) {
      data.removeNode(id);
    } else if (data.isEditMode && data.removeLastNode) {
      data.removeLastNode();
      data.removeLastNode();
    }
  };

  const actionTitle = t('workflows.send_notification');
  const actionIcon = data.actionData?.icon || 'notifications';

  const updateNodeConfiguration = () => {
    dispatch(
      setNodeConfiguration({
        nodeId: id,
        nodeType: 'action',
        data: {
          actionType: 'notification',
          title: titleValue,
          returnValue: messageValue,
          message: messageValue,
        },
      }),
    );
  };

  const handleTitleChange = (value: string) => {
    if (!data.isEditMode) return;
    setTitleValue(value);
    updateNodeConfiguration();
  };

  const handleMessageChange = (value: string) => {
    if (!data.isEditMode) return;
    setMessageValue(value);
    updateNodeConfiguration();
  };

  return (
    <div className="surface-elevated rounded-base border border-subtle relative overflow-scroll w-[45rem]">
      <div className="w-full">
        <div className="py-16 px-20">
          <NodeHeader
            title={actionTitle}
            subtitle="Aktion"
            icon={actionIcon}
            onActionClick={handleResetClick}
            isEditMode={data.isEditMode}
          />
        </div>
        <div className="px-20">
          <div className="mb-16">
            <label className="block typo-text-12--bold text-full mb-8">{t('workflows.title')}</label>
            <Input
              value={titleValue}
              onChange={e => handleTitleChange(e.target.value)}
              placeholder={t('workflows.notification_title_placeholder')}
              className="w-full"
              disabled={!data.isEditMode}
            />
          </div>
        </div>
        <div className="px-20 pb-20">
          <div>
            <label className="block typo-text-12--bold text-full mb-8">{t('workflows.message')}</label>
            <textarea
              value={messageValue}
              onChange={e => handleMessageChange(e.target.value)}
              placeholder={t('workflows.notification_message_placeholder')}
              className={`w-full min-h-[14rem] px-12 py-8 border border-subtle rounded-base typo-text-14 text-full  resize-none ${!data.isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
              rows={4}
              disabled={!data.isEditMode}
              readOnly={!data.isEditMode}
            />
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Right}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            right: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle type="source" position={Position.Top} id={`${id}`} style={{ opacity: 0, top: '37px' }} />
        <Handle type="source" position={Position.Bottom} id={`${id}`} style={{ opacity: 0, top: '37px' }} />
        <Handle
          type="target"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
      </div>
    </div>
  );
};

export default NotificationBuilderNode;
